/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */
import React from 'react';
import _ from 'lodash';
import { toast } from 'react-toastify';

import { request } from '@mairistem/tools';
import { Divider, Grid, Icon } from '@mairistem/composants';

import { getStatistiquesDashboard } from '@/routes';
import { useEntiteStore } from '@/zustand';
import { RecenseModal } from '@/components/Dashboard/Liste';

interface ModalConfig {
  open: boolean;
  title: string;
  typeRecensement?:
    | 'all'
    | 'recenses'
    | 'recenses-regularises'
    | 'non-recenses'
    | 'recenses-recenses-regularises';
  doubleNationalite?: boolean;
  periode?: string;
}

interface VueStatistiqueTableauProps {
  annee: number;
  setLoading: (loading: boolean) => void;
}

const VueStatistiqueTableau = (props: VueStatistiqueTableauProps) => {
  const { annee, setLoading } = props;

  const [data, setData] =
    React.useState<Record<string, Record<string, number>>>();
  const [modalConfig, setModalConfig] = React.useState<ModalConfig>({
    open: false,
    title: '',
  });
  const blocked = useEntiteStore((state) => state.blocked);

  const getStatistiques = React.useCallback(
    (year) => {
      setLoading(true);
      request
        .get(getStatistiquesDashboard(year))
        .then((response: Record<string, Record<string, number>>) => {
          setData(response);
        })
        .catch(() => {
          toast(
            'Une erreur est survenue lors de la récupération de la synthèse',
            { type: 'error' },
          );
        })
        .finally(() => setLoading(false));
    },
    [setLoading],
  );

  React.useEffect(() => {
    if (!blocked) {
      getStatistiques(annee);
    }
  }, [blocked, annee, getStatistiques]);

  const rowStyle = {
    style: {
      padding: 0,
    },
  };

  const dividerStyle = {
    style: {
      marginTop: 10,
      marginBottom: 10,
      backgroundColor: 'lightgrey',
    },
  };

  const clickableStyle = {
    cursor: 'pointer',
    textDecoration: 'underline',
  };

  const openModal = React.useCallback((config: Omit<ModalConfig, 'open'>) => {
    setModalConfig({ open: true, ...config });
  }, []);

  const renderStatValue = (
    value: number,
    config: Omit<ModalConfig, 'open'>,
    bold = false,
  ) => {
    const handleClick = () => {
      if (value >= 1) {
        openModal(config);
      }
    };
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openModal(config);
      }
    };

    const content = (
      <span
        style={value >= 1 ? clickableStyle : undefined}
        onClick={value >= 1 ? handleClick : undefined}
        onKeyDown={value >= 1 ? handleKeyDown : undefined}
      >
        {value}
      </span>
    );

    return bold ? <b>{content}</b> : content;
  };

  const closeModal = () => {
    setModalConfig({ open: false, title: '' });
  };

  const getTrimestreLabel = (num: number) => (num === 1 ? '1er' : `${num}ème`);

  return (
    <>
      <Grid
        style={{
          marginTop: '1.5em',
        }}
      >
        <Grid.Row {...rowStyle}>
          <Grid.Column width={5}>
            <b>Trimestre</b>
          </Grid.Column>
          <Grid.Column width={1} />
          <Grid.Column width={2} textAlign="center">
            <b>1er</b>
          </Grid.Column>
          {_.map([2, 3, 4], (num) => (
            <Grid.Column key={num} width={2} textAlign="center">
              <b>
                {num}
                ème
              </b>
            </Grid.Column>
          ))}
          <Grid.Column width={2} textAlign="center">
            <b>Total</b>
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />
        <Grid.Row {...rowStyle}>
          <Grid.Column
            width={6}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Icon name="CircleCheckBig" iconSet="Lucide" color="green" />
            &nbsp;
            <b>Nombre de jeunes recensés</b>
          </Grid.Column>
          {_.map([1, 2, 3, 4], (num) => {
            const count =
              (_.toNumber(data?.[`trimestre${num}`]?.recenses) || 0) +
              (_.toNumber(data?.[`trimestre${num}`]?.recensesRegularises) || 0);
            return (
              <Grid.Column key={num} width={2} textAlign="center">
                {renderStatValue(
                  count,
                  {
                    title: `Jeunes recensés - ${getTrimestreLabel(num)} trimestre ${annee}`,
                    typeRecensement: 'recenses-recenses-regularises',
                    periode: `${annee}-${num}`,
                  },
                  true,
                )}
              </Grid.Column>
            );
          })}
          <Grid.Column width={2} textAlign="center">
            {renderStatValue(
              _.sumBy(
                [1, 2, 3, 4],
                (num) =>
                  (_.toNumber(data?.[`trimestre${num}`]?.recenses) || 0) +
                  (_.toNumber(data?.[`trimestre${num}`]?.recensesRegularises) ||
                    0),
              ),
              {
                title: `Jeunes recensés - Total ${annee}`,
                typeRecensement: 'recenses-recenses-regularises',
                periode: `${annee}`,
              },
              true,
            )}
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />
        <Grid.Row {...rowStyle}>
          <Grid.Column width={5} style={{ paddingLeft: '2rem' }}>
            &nbsp;&nbsp;&nbsp;
            <Icon name="circle" size="tiny" color="grey" />
            sur liste de recensement
          </Grid.Column>
          <Grid.Column width={1} />

          {_.map([1, 2, 3, 4], (num) => {
            const count = _.toNumber(data?.[`trimestre${num}`]?.recenses) || 0;
            return (
              <Grid.Column key={num} width={2} textAlign="center">
                {renderStatValue(count, {
                  title: `Jeunes recensés sur liste - ${getTrimestreLabel(num)} trimestre ${annee}`,
                  typeRecensement: 'recenses',
                  periode: `${annee}-${num}`,
                })}
              </Grid.Column>
            );
          })}
          <Grid.Column width={2} textAlign="center">
            {renderStatValue(
              _.sumBy(
                [1, 2, 3, 4],
                (num) => _.toNumber(data?.[`trimestre${num}`]?.recenses) || 0,
              ),
              {
                title: `Jeunes recensés sur liste - Total ${annee}`,
                typeRecensement: 'recenses',
                periode: `${annee}`,
              },
            )}
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />
        <Grid.Row {...rowStyle}>
          <Grid.Column width={5} style={{ paddingLeft: '2rem' }}>
            &nbsp;&nbsp;&nbsp;
            <Icon name="circle" size="tiny" color="grey" />
            sur liste de régularisation
          </Grid.Column>
          <Grid.Column width={1} />
          {_.map([1, 2, 3, 4], (num) => {
            const count =
              _.toNumber(data?.[`trimestre${num}`]?.recensesRegularises) || 0;
            return (
              <Grid.Column key={num} width={2} textAlign="center">
                {renderStatValue(count, {
                  title: `Jeunes recensés régularisés - ${getTrimestreLabel(num)} trimestre ${annee}`,
                  typeRecensement: 'recenses-regularises',
                  periode: `${annee}-${num}`,
                })}
              </Grid.Column>
            );
          })}
          <Grid.Column width={2} textAlign="center">
            {renderStatValue(
              _.sumBy(
                [1, 2, 3, 4],
                (num) =>
                  _.toNumber(data?.[`trimestre${num}`]?.recensesRegularises) ||
                  0,
              ),
              {
                title: `Jeunes recensés régularisés - Total ${annee}`,
                typeRecensement: 'recenses-regularises',
                periode: `${annee}`,
              },
            )}
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />
        <Grid.Row {...rowStyle}>
          <Grid.Column
            width={6}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Icon name="CircleX" iconSet="Lucide" color="red" />
            &nbsp;
            <b>Nombre de jeunes non recensés</b>
          </Grid.Column>
          {_.map([1, 2, 3, 4], (num) => {
            const count =
              _.toNumber(data?.[`trimestre${num}`]?.nonRecenses) || 0;
            return (
              <Grid.Column key={num} width={2} textAlign="center">
                {renderStatValue(
                  count,
                  {
                    title: `Jeunes non recensés - ${getTrimestreLabel(num)} trimestre ${annee}`,
                    typeRecensement: 'non-recenses',
                    periode: `${annee}-${num}`,
                  },
                  true,
                )}
              </Grid.Column>
            );
          })}
          <Grid.Column width={2} textAlign="center">
            {renderStatValue(
              _.sumBy(
                [1, 2, 3, 4],
                (num) =>
                  _.toNumber(data?.[`trimestre${num}`]?.nonRecenses) || 0,
              ),
              {
                title: `Jeunes non recensés - Total ${annee}`,
                typeRecensement: 'non-recenses',
                periode: `${annee}`,
              },
              true,
            )}
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />
        <Grid.Row {...rowStyle}>
          <Grid.Column
            width={6}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Icon name="CircleAlert" iconSet="Lucide" color="orange" />
            &nbsp;
            <b>Nombre de jeunes ayant une double nationalité</b>
          </Grid.Column>
          {_.map([1, 2, 3, 4], (num) => {
            const count =
              _.toNumber(data?.[`trimestre${num}`]?.doubleNationalite) || 0;
            return (
              <Grid.Column key={num} width={2} textAlign="center">
                {renderStatValue(
                  count,
                  {
                    title: `Jeunes avec double nationalité - ${getTrimestreLabel(num)} trimestre ${annee}`,
                    doubleNationalite: true,
                    periode: `${annee}-${num}`,
                  },
                  true,
                )}
              </Grid.Column>
            );
          })}
          <Grid.Column width={2} textAlign="center">
            {renderStatValue(
              _.sumBy(
                [1, 2, 3, 4],
                (num) =>
                  _.toNumber(data?.[`trimestre${num}`]?.doubleNationalite) || 0,
              ),
              {
                title: `Jeunes avec double nationalité - Total ${annee}`,
                doubleNationalite: true,
                periode: `${annee}`,
              },
              true,
            )}
          </Grid.Column>
        </Grid.Row>

        <Divider {...dividerStyle} />
        <Grid.Row {...rowStyle}>
          <Grid.Column
            width={6}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <div style={{ width: 24 }} />
            &nbsp;
            <b>Total des recensés et non recensés</b>
          </Grid.Column>
          {_.map([1, 2, 3, 4], (num) => {
            const count =
              (_.toNumber(data?.[`trimestre${num}`]?.recenses) || 0) +
              (_.toNumber(data?.[`trimestre${num}`]?.recensesRegularises) ||
                0) +
              (_.toNumber(data?.[`trimestre${num}`]?.nonRecenses) || 0);
            return (
              <Grid.Column key={num} width={2} textAlign="center">
                {renderStatValue(
                  count,
                  {
                    title: `Total des recensés et non recensés - ${getTrimestreLabel(num)} trimestre ${annee}`,
                    typeRecensement: 'all',
                    periode: `${annee}-${num}`,
                  },
                  true,
                )}
              </Grid.Column>
            );
          })}
          <Grid.Column width={2} textAlign="center">
            {renderStatValue(
              _.sumBy(
                [1, 2, 3, 4],
                (num) =>
                  (_.toNumber(data?.[`trimestre${num}`]?.recenses) || 0) +
                  (_.toNumber(data?.[`trimestre${num}`]?.recensesRegularises) ||
                    0) +
                  (_.toNumber(data?.[`trimestre${num}`]?.nonRecenses) || 0),
              ),
              {
                title: `Total des recensés et non recensés - Total ${annee}`,
                typeRecensement: 'all',
                periode: `${annee}`,
              },
              true,
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <RecenseModal
        open={modalConfig.open}
        title={modalConfig.title}
        typeRecensement={modalConfig.typeRecensement}
        doubleNationalite={modalConfig.doubleNationalite}
        periode={modalConfig.periode}
        onClose={closeModal}
      />
    </>
  );
};

export default VueStatistiqueTableau;
